import { useState, type FormEvent, type JSX } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { usePostHog } from '@posthog/react';
import { Button, Card, Field, Input } from '@viniciomendesr/rayrovisk-design-system';
import { signInWithPassword } from '../api/password';
import { useSession } from '../auth/SessionProvider';
import { supabase } from '../auth/supabase';
import { AuthLayout } from './PasswordScreenParts';

/**
 * The second door (ADR-0019). The magic link stays the first one, and this
 * exists for the person who opens the product every week and should not have to
 * ask for an email to read what she already owns.
 */
export function PasswordSignInScreen(): JSX.Element | null {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const posthog = usePostHog();

  const location = useLocation();
  const { session, loading } = useSession();

  // The mirror of RequireSession, and it waits the same way: rendering the form
  // before the first session answer arrives asks a signed-in buyer for a
  // password she may never have set, on the door that is not the default one.
  if (loading) {
    return null;
  }

  if (session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/biblioteca'} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);

    try {
      const session = await signInWithPassword(email, password);

      // Handed straight to the SDK, which owns persistence and the background
      // refresh. Keeping a copy here would be a second source of truth that
      // goes stale in an hour.
      const client = supabase();
      if (!client) {
        // No client means no configured project, and the tokens that just
        // arrived have nowhere to live. Walking on would navigate into a guard
        // that bounces straight back here, which reads exactly like a wrong
        // password — the one thing the service just proved it was not.
        throw new Error(
          'Não conseguimos abrir a sessão. É uma falha de configuração nossa: ' +
            'tentar de novo não resolve, fale com a gente.'
        );
      }

      await client.auth.setSession({
        access_token: session.accessToken,
        refresh_token: session.refreshToken
      });

      posthog?.capture('password_sign_in_completed');
      navigate('/biblioteca', { replace: true });
    } catch (error) {
      // The service's own sentence. The transport's is "Failed to fetch", which
      // is true and useless. It answers one body for a wrong password and for an
      // address it has never seen, so repeating it discloses nothing (§12.3).
      setProblem(
        error instanceof Error && error.message
          ? error.message
          : 'Não conseguimos entrar agora. Tente de novo.'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Card layer="functional" elevation="raised" padding="lg">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h1 style={{ margin: 0, font: '400 26px/1.15 var(--rvk-font-expressive)' }}>
              Entrar com senha
            </h1>
            <p
              style={{
                margin: 0,
                font: '400 13.5px/1.55 var(--rvk-font-functional)',
                color: 'var(--rvk-text-secondary)'
              }}
            >
              Se você já definiu uma senha, entre por aqui. O link por e-mail continua funcionando.
            </p>
          </div>

          <Field label="E-mail">
            <Input
              type="email"
              required
              autoComplete="email"
              placeholder="voce@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Senha" error={problem ?? undefined}>
            <Input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Entrar
          </Button>

          <div style={{ height: 1, background: 'var(--rvk-border-subtle)' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <Link to="/senha/recuperar">
              <Button variant="ghost" size="sm" type="button">
                Esqueci a minha senha
              </Button>
            </Link>
            {/* The door that needs no memory. Someone locked out of this one has
                to be a click away from the one that always opens. */}
            <Link to="/entrar">
              <Button variant="ghost" size="sm" type="button">
                Entrar com link por e-mail
              </Button>
            </Link>
          </div>
        </form>
      </Card>

      <p
        style={{
          margin: 0,
          textAlign: 'center',
          font: '400 12px/1.6 var(--rvk-font-functional)',
          color: 'var(--rvk-text-tertiary)'
        }}
      >
        A senha é opcional. Ela se define dentro da sua conta, depois de entrar.
      </p>
    </AuthLayout>
  );
}
